'use client';

import { getContact, getContacts } from '@/api/contacts';
import { getIcp, getIcps } from '@/api/icps';
import { getPersona, getPersonas } from '@/api/personas';
import { getTargetPages } from '@/api/target-pages';
import QualificationPill from '@/app/components/common/QualificationPill';
import {
  contactOptionItem,
  contactTagItem,
} from '@/app/components/query-builder/contactFieldPresets';
import { createTargetPageQueryField } from '@/app/components/query-builder/field-presets';
import { extractRuleValues } from '@/app/components/query-builder/query-utils';
import type { Operator, QueryDefinition, QueryField } from '@/app/components/query-builder/types';
import { useResolveMissingSelections } from '@/app/components/query-builder/useResolveMissingSelections';
import type { Contact } from '@/types/contact';
import type { Icp } from '@/types/icp';
import type { Persona } from '@/types/persona';
import type { TargetPage } from '@/types/targetPage';
import { keepPreviousData, useQueries } from '@tanstack/react-query';
import { useTranslations } from 'next-intl';
import { useMemo, useState } from 'react';
import { createSignalDateQueryField, createSignalTypeQueryField } from './signalQueryFieldPresets';

import { SIGNAL_FILTER_CONFIGS } from './signalFilterConfigs';

type Options = {
  enabled?: boolean;
  currentQuery?: QueryDefinition;
  signalType?: 'website' | 'market' | 'linkedin';
  targetPage?: {
    name?: string;
    operators?: Operator[];
    getOptionValue?: (targetPage: TargetPage) => string;
  };
};

export default function useWebsiteSignalQueryFields(
  clientId: string,
  { enabled = true, currentQuery, signalType = 'website', targetPage }: Options = {}
) {
  const t = useTranslations('signals');
  const [contactSearch, setContactSearch] = useState('');
  const queries = useQueries({
    queries: [
      {
        queryKey: ['website-filter-contacts', clientId, contactSearch],
        queryFn: () => getContacts({ clientId, limit: 100, search: contactSearch }),
        enabled: enabled && !!clientId,
        placeholderData: keepPreviousData,
      },
      {
        queryKey: ['website-filter-personas', clientId],
        queryFn: () => getPersonas({ clientId, limit: 100 }),
        enabled: enabled && !!clientId,
      },
      {
        queryKey: ['website-filter-icps', clientId],
        queryFn: () => getIcps({ clientId, limit: 100 }),
        enabled: enabled && !!clientId,
      },
      {
        queryKey: ['website-filter-target-pages', clientId],
        queryFn: () => getTargetPages({ clientId, limit: 100, pageStatus: 'active' }),
        enabled: enabled && !!clientId,
      },
    ],
  });
  const contactsData = queries[0].data;
  const contactsSearchLoading = queries[0].isFetching;
  const personasData = queries[1].data;
  const icpsData = queries[2].data;
  const targetPagesData = queries[3].data;

  const contacts = useMemo(() => (contactsData?.data || []) as Contact[], [contactsData]);
  const personas = useMemo(() => (personasData?.data || []) as Persona[], [personasData]);
  const icps = useMemo(() => (icpsData?.data || []) as Icp[], [icpsData]);
  const targetPages = useMemo(
    () => (targetPagesData?.data || []) as TargetPage[],
    [targetPagesData]
  );

  const selectedContactIds = useMemo(
    () => (currentQuery ? extractRuleValues(currentQuery, 'contact_id') : []),
    [currentQuery]
  );
  const selectedIcpIds = useMemo(
    () => (currentQuery ? extractRuleValues(currentQuery, 'icp_id') : []),
    [currentQuery]
  );
  const selectedPersonaIds = useMemo(
    () => (currentQuery ? extractRuleValues(currentQuery, 'persona_id') : []),
    [currentQuery]
  );

  const missingContacts = useResolveMissingSelections<Contact>({
    clientId,
    selectedIds: selectedContactIds,
    availableIds: useMemo(() => new Set(contacts.map(contact => contact.id)), [contacts]),
    fetchOne: getContact,
    queryKeyPrefix: 'website-filter-missing-contact',
    enabled,
  });
  const missingIcps = useResolveMissingSelections<Icp>({
    clientId,
    selectedIds: selectedIcpIds,
    availableIds: useMemo(() => new Set(icps.map(icp => icp.id)), [icps]),
    fetchOne: getIcp,
    queryKeyPrefix: 'website-filter-missing-icp',
    enabled,
  });
  const missingPersonas = useResolveMissingSelections<Persona>({
    clientId,
    selectedIds: selectedPersonaIds,
    availableIds: useMemo(() => new Set(personas.map(persona => persona.id)), [personas]),
    fetchOne: getPersona,
    queryKeyPrefix: 'website-filter-missing-persona',
    enabled,
  });

  const allContacts = useMemo(() => [...missingContacts, ...contacts], [missingContacts, contacts]);
  const allIcps = useMemo(() => [...missingIcps, ...icps], [missingIcps, icps]);
  const allPersonas = useMemo(() => [...missingPersonas, ...personas], [missingPersonas, personas]);

  return useMemo<QueryField[]>(() => {
    if (signalType === 'linkedin') {
      const config = SIGNAL_FILTER_CONFIGS.linkedin;
      return [
        {
          name: 'action',
          label: t('filters.action'),
          type: 'select' as const,
          operators: ['=', 'in', 'notIn'] as Operator[],
          options: config.triggers.map(value => ({
            value,
            label: t(`actions.${value}` as never),
          })),
          defaultValue: '',
        },
        createSignalDateQueryField(),
      ];
    }

    return [
      {
        name: 'contact_id',
        label: t('filters.buyer'),
        type: 'select',
        operators: ['=', 'in'],
        options: allContacts.map(contact => ({
          label: `${contact.contact_info.name.first} ${contact.contact_info.name.last}`.trim(),
          value: contact.id,
          meta: { photoUrl: contact.contact_info.photo_url },
        })),
        optionItem: contactOptionItem,
        tagItem: contactTagItem,
        onSearch: setContactSearch,
        searchLoading: contactsSearchLoading,
        defaultValue: '',
      },
      {
        name: 'icp_id',
        label: t('filters.icp'),
        type: 'select',
        operators: ['=', 'in'],
        options: allIcps.map(icp => ({ label: icp.short_name || icp.name, value: icp.id })),
        optionItem: option => <QualificationPill type="icp" label={option.label} />,
        defaultValue: '',
      },
      {
        name: 'persona_id',
        label: t('filters.persona'),
        type: 'select',
        operators: ['=', 'in'],
        options: allPersonas.map(persona => ({
          label: persona.short_name || persona.name,
          value: persona.id,
        })),
        optionItem: option => <QualificationPill type="persona" label={option.label} />,
        defaultValue: '',
      },
      {
        name: 'location',
        label: t('filters.location'),
        type: 'text',
        operators: ['=', 'in'],
        defaultValue: '',
        placeholder: t('filters.locationsPlaceholder'),
      },
      ...(signalType === 'market'
        ? [
            {
              name: 'action',
              label: t('filters.action'),
              type: 'select' as const,
              operators: ['=', 'in', 'notIn'] as Operator[],
              options: SIGNAL_FILTER_CONFIGS.market.triggers.map(value => ({
                value,
                label: t(`actions.${value}` as never),
              })),
              defaultValue: '',
            },
          ]
        : []),
      createSignalTypeQueryField(),
      createSignalDateQueryField(),
      createTargetPageQueryField(targetPages, {
        ...targetPage,
        matchTypeLabels: {
          exact: t('filters.matchTypes.exact'),
          contains: t('filters.matchTypes.contains'),
          starts_with: t('filters.matchTypes.startsWith'),
          ends_with: t('filters.matchTypes.endsWith'),
        },
      }),
    ];
  }, [
    allContacts,
    allIcps,
    allPersonas,
    contactsSearchLoading,
    signalType,
    t,
    targetPage,
    targetPages,
  ]);
}
